<template>
  <div class="contents">
    <div class="left">
      <div>
        <NoticeBar />
      </div>
      
      <!-- 工具栏区域 -->
      <div class="toolbar">
        <div class="toolbar-left">
          <el-button 
            type="primary" 
            icon="el-icon-document-checked" 
            class="action-btn"
            :disabled="!content.trim()"
            @click="startProofread">
            立即校对
          </el-button>
          <el-button 
            type="danger" 
            plain 
            icon="el-icon-delete" 
            class="action-btn"
            :disabled="!content.trim()"
            @click="clearEditor">
            清空内容
          </el-button>
          <!-- <el-button 
            type="success" 
            plain 
            icon="el-icon-plus" 
            class="action-btn"
            @click="addToWordbank">
            加入词库
          </el-button> -->
          <el-button 
            type="warning" 
            plain 
            icon="el-icon-upload2" 
            class="action-btn"
            @click="triggerFileInput">
            导入Word
          </el-button>
          <el-button 
            type="info" 
            plain 
            icon="el-icon-download" 
            class="action-btn"
            :disabled="!content.trim()"
            @click="exportToWord">
            导出Word
          </el-button>
          <!-- 隐藏的文件输入框 -->
          <input 
            type="file" 
            ref="fileInput" 
            accept=".doc,.docx"
            style="display: none"
            @change="handleFileImport"
          />
        </div>
        
        <div class="word-counter">
          <span class="counter-text">字数统计：</span>
          <span class="counter-number" :class="{ 'over-limit': wordCount > 1000 }">
            {{ wordCount }}
          </span>
          <span class="counter-limit">/1000</span>
        </div>
      </div>

      <!-- 编辑器区域 -->
      <div class="editor-container">
        <div
          class="editor"
          contenteditable="true"
          :data-placeholder="'请输入要校对的文本内容，最多1000字...'"
          @input="handleInput"
          @paste="handlePaste"
          @keydown="handleKeydown"
          ref="editor"
          @focus="isFocused = true"
          @blur="checkContent"
          @mouseup="handleTextSelection"
          @keyup="handleTextSelection"
          v-html="text"
        >
        </div>
        
        <!-- 字数超限提示 -->
        <div v-if="wordCount > 1000" class="limit-warning">
          <i class="el-icon-warning"></i>
          内容已超出1000字限制，请删减内容
        </div>
      </div>
    </div>
    
    <div class="right">
      <!-- 统计信息卡片 -->
      <div class="stats-card">
        <div class="stats-header">
          <h3>校对统计</h3>
        </div>
        <div class="stats-content">
          <div class="stat-item total">
            <div class="stat-number">{{ totalIssues }}</div>
            <div class="stat-label">全部提示</div>
          </div>
          <div class="stats-grid">
            <div class="stat-item high-risk">
              <div class="stat-number">{{ highRiskCount }}</div>
              <div class="stat-label">删除</div>
            </div>
            <div class="stat-item low-risk">
              <div class="stat-number">{{ lowRiskCount }}</div>
              <div class="stat-label">插入</div>
            </div>
            <div class="stat-item sensitive">
              <div class="stat-number">{{ sensitiveCount }}</div>
              <div class="stat-label">修改</div>
            </div>
            <div class="stat-item punctuation">
              <div class="stat-number">{{ punctuationCount }}</div>
              <div class="stat-label">替换</div>
            </div>
            <div class="stat-item grammar">
              <div class="stat-number">{{ grammarCount }}</div>
              <div class="stat-label">警示</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 问题列表 -->
      <div class="issues-section">
        <h3>修改建议</h3>
        <div class="issues-list" v-if="issues.length > 0">
          <div 
            v-for="(issue, index) in issues" 
            :key="index"
            class="issue-item" 
            :class="issue.type"
            @mouseenter="hoverIssue(index)"
            @mouseleave="clearHover"
            @click="highlightIssue(issue, index)">
            <div class="issue-content">
              <div class="issue-text">
                <span class="original-text">{{ issue.original }}</span>
                <i class="el-icon-arrow-right"></i>
                <span class="suggested-text">{{ issue.suggestion }}</span>
              </div>
              <div class="issue-description">{{ issue.description }}</div>
            </div>
            <div class="issue-actions" v-if="issue.type != 'grammar'">
              <el-button type="text" size="mini" @click.stop="acceptSuggestion(issue, index)">
                <i class="el-icon-check"></i>
              </el-button>
              <el-button type="text" size="mini" @click.stop="ignoreIssue(index)">
                <i class="el-icon-close"></i>
              </el-button>
            </div>
          </div>
        </div>
        <div v-else class="no-issues">
          <i class="el-icon-circle-check"></i>
          <p>暂无发现问题</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NoticeBar from '@/components/NoticeBar.vue'

export default {
  name: 'ContentProofread',
  components: {
    NoticeBar
  },
  data() {
    return {
      isFocused: false,
      content: '',
      wordCount: 0,
      selectedText: '',
      currentHighlightIndex: -1,
      issues: []
    }
  },
  computed: {
    totalIssues() {
      return this.issues.length
    },
    highRiskCount() {
      return this.issues.filter(issue => issue.type === 'error').length
    },
    lowRiskCount() {
      return this.issues.filter(issue => issue.type === 'warning').length
    },
    sensitiveCount() {
      return this.issues.filter(issue => issue.type === 'sensitive').length
    },
    punctuationCount() {
      return this.issues.filter(issue => issue.type === 'punctuation').length
    },
    grammarCount() {
      return this.issues.filter(issue => issue.type === 'grammar').length
    }
  },
  created(){
    if(!localStorage.getItem('token')){
      this.$router.push('/login')
    }
  },
  mounted() {
    this.applyHighlighting()
  },
  methods: {
    hoverIssue(index) {
      this.clearHover();
      const highlightEl = this.$refs.editor.querySelector(`[data-issue-index="${index}"]`);
      if (highlightEl) {
        highlightEl.classList.add('hovered-highlight');
        // highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    clearHover() {
      const highlights = this.$refs.editor.querySelectorAll('.hovered-highlight');
      highlights.forEach(el => el.classList.remove('hovered-highlight'));
    },

    handlePaste(e) {
      e.preventDefault()
      const text = (e.clipboardData || window.clipboardData).getData('text')
      
      const selection = window.getSelection()
      if (!selection.rangeCount) return
      
      selection.deleteFromDocument()
      
      const textNode = document.createTextNode(text)
      const range = selection.getRangeAt(0)
      range.insertNode(textNode)
      
      range.setStartAfter(textNode)
      range.setEndAfter(textNode)
      selection.removeAllRanges()
      selection.addRange(range)
      
      this.handleInput()
    },

    handleInput() {
      const text = this.$refs.editor.innerText
      this.content = text
      this.wordCount = text.length
      
      if (this.wordCount > 1000) {
        this.content = this.content.substring(0, 1000)
        this.$refs.editor.innerText = this.content
        this.wordCount = 1000
        this.placeCursorAtEnd()
        this.$message.warning('内容已达到1000字上限')
      }
      
      console.log('用户输入的内容是：', text)
    },

    handleKeydown(e) {
      if (this.wordCount >= 1000 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault()
      }
    },

    handleTextSelection() {
      const selection = window.getSelection()
      this.selectedText = selection.toString().trim()
    },

    placeCursorAtEnd() {
      this.$nextTick(() => {
        const range = document.createRange()
        const selection = window.getSelection()
        range.selectNodeContents(this.$refs.editor)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      })
    },

    async startProofread() {
      if (!this.content.trim()) {
        this.$message.warning('请先输入要校对的内容')
        return
      }
      
      this.$message.success('开始校对...')
      const res = await this.$api.post('/api/ercor/ecnet', {
        text: this.content
      })
      if(res.code != 1){
        this.$message.warning(res.msg);
        return
      }
      this.issues = res.data.issues
      console.log('接收到的 issues:', this.issues)
      this.applyHighlighting()
    },

    clearEditor() {
      this.$confirm('确定要清空所有内容吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$refs.editor.innerHTML = ''
        this.content = ''
        this.wordCount = 0
        this.selectedText = ''
        this.issues = []
        this.$message.success('内容已清空')
        this.$nextTick(() => this.$refs.editor.focus())
      }).catch(() => {})
    },

    addToWordbank() {
      if (!this.selectedText) {
        this.$message.warning('请先选中要加入词库的词语')
        return
      }
      
      this.$confirm(`确定将"${this.selectedText}"加入词库吗？`, '加入词库', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        console.log('加入词库:', this.selectedText)
        this.$message.success(`"${this.selectedText}"已加入词库`)
      }).catch(() => {})
    },

    // 触发文件选择
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    // 处理文件导入
    async handleFileImport(event) {
      const file = event.target.files[0]
      if (!file) return

      // 检查文件类型
      const fileName = file.name.toLowerCase()
      if (!fileName.endsWith('.doc') && !fileName.endsWith('.docx')) {
        this.$message.error('请选择Word文档（.doc或.docx格式）')
        this.$refs.fileInput.value = ''
        return
      }

      // 显示加载提示
      const loading = this.$loading({
        lock: true,
        text: '正在导入文档...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        // 读取文件为ArrayBuffer
        const arrayBuffer = await file.arrayBuffer()
        
        if (fileName.endsWith('.docx')) {
          // 使用JSZip解析docx文件（docx本质是zip压缩包）
          const text = await this.extractTextFromDocx(arrayBuffer)
          
          // 限制1000字符
          let finalText = text.trim()
          if (finalText.length > 1000) {
            finalText = finalText.substring(0, 1000)
            this.$message.warning('文档内容超过1000字，已自动截取前1000字')
          }
          
          this.content = finalText
          this.wordCount = finalText.length
          this.$refs.editor.innerText = finalText
          this.issues = [] // 清空之前的校对结果
          
          this.$message.success(`文档导入成功，共${finalText.length}字`)
        } else {
          // .doc格式暂不支持
          this.$message.error('暂不支持.doc格式，请将文档另存为.docx格式后重试')
        }
      } catch (error) {
        console.error('导入失败:', error)
        this.$message.error('文档导入失败：' + error.message)
      } finally {
        loading.close()
        // 清空文件输入框，允许重复导入同一文件
        this.$refs.fileInput.value = ''
      }
    },

    // 从docx文件中提取文本（使用原生API）
    async extractTextFromDocx(arrayBuffer) {
      try {
        // 动态加载JSZip库
        if (!window.JSZip) {
          await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js')
        }

        const zip = await JSZip.loadAsync(arrayBuffer)
        const doc = await zip.file('word/document.xml').async('string')
        
        // 使用正则提取文本内容
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(doc, 'text/xml')
        const textElements = xmlDoc.getElementsByTagName('w:t')
        
        let text = ''
        for (let i = 0; i < textElements.length; i++) {
          text += textElements[i].textContent
        }
        
        return text
      } catch (error) {
        throw new Error('解析文档失败，请确保文件格式正确')
      }
    },

    // 动态加载外部脚本
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = src
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    },

    exportToWord() {
      if (!this.content.trim()) {
        this.$message.warning('请先输入内容再导出')
        return
      }
      
      try {
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>AI数智全擎-内容校对</title>
            <style>
              body { font-family: '微软雅黑', Arial, sans-serif; line-height: 1.6; margin: 40px; }
              .header { text-align: center; margin-bottom: 30px; }
              .content { white-space: pre-wrap; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>内容校对文档</h1>
              <p>生成时间：${new Date().toLocaleString()}</p>
              <p>字数统计：${this.wordCount}字</p>
            </div>
            <div class="content">${this.content}</div>
          </body>
          </html>
        `
        
        const blob = new Blob([htmlContent], {
          type: 'application/msword'
        })
        
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `校对文档_${new Date().getTime()}.doc`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        this.$message.success('文档导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败，请重试')
      }
    },

    findNodeForOffset(container, offset) {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
      let node;
      let pos = 0;
      
      while ((node = walker.nextNode())) {
        const len = node.nodeValue.length;
        if (pos + len >= offset) {
          return { node, nodeOffset: offset - pos };
        }
        pos += len;
      }
      
      if (node) {
        return { node, nodeOffset: node.nodeValue.length };
      }
      return null;
    },

    applyHighlighting() {
      const editor = this.$refs.editor;
      if (!editor) return;

      const safeHtml = this.content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      editor.innerHTML = safeHtml;

      if (!this.issues || !this.issues.length) return;

      const fragments = [];

      this.issues.forEach((issue, issueIndex) => {
        console.log('处理 issue:', issue);
        
        if (issue.position && Array.isArray(issue.position) && issue.position.length === 2) {
          const [rangeStart, rangeEnd] = issue.position;
          const operationType = this.getOperationType(issue);
          
          let actualStart = rangeStart;
          let actualEnd = rangeEnd;
          
          if (issue.original && issue.original !== '-') {
            const rangeText = this.content.substring(rangeStart, rangeEnd);
            const originalIndex = rangeText.indexOf(issue.original);
            
            if (originalIndex !== -1) {
              actualStart = rangeStart + originalIndex;
              actualEnd = actualStart + issue.original.length;
              console.log(`在范围 [${rangeStart}, ${rangeEnd}] 内找到 "${issue.original}" 的位置: [${actualStart}, ${actualEnd}]`);
            } else {
              console.warn(`在范围 [${rangeStart}, ${rangeEnd}] 内未找到 "${issue.original}"`);
              console.log(`范围文本: "${rangeText}"`);
              console.log(`查找内容: "${issue.original}"`);
              return;
            }
          } else if (operationType === 'delete') {
            actualStart = rangeStart;
            actualEnd = rangeEnd;
          }
          
          issue.exactPosition = [actualStart, actualEnd];
          
          fragments.push({
            start: actualStart,
            end: actualEnd,
            type: issue.type || 'grammar',
            issueIndex,
            description: issue.description || '',
            original: issue.original,
            operationType: operationType
          });
        }
      });

      console.log('生成的 fragments:', fragments);
      console.log('内容长度:', this.content.length);

      fragments.sort((a, b) => b.start - a.start);

      fragments.forEach(frag => {
        if (typeof frag.start !== 'number' || typeof frag.end !== 'number' || frag.start > frag.end) {
          console.warn('索引不合理:', frag);
          return;
        }

        if (frag.start >= this.content.length || frag.end > this.content.length) {
          console.warn('索引超出范围:', frag, '内容长度:', this.content.length);
          return;
        }

        const actualText = this.content.substring(frag.start, frag.end);
        console.log(`Fragment ${frag.issueIndex} (${frag.operationType}): 期望="${frag.original}", 实际="${actualText}", 位置=[${frag.start}, ${frag.end}]`);

        const startInfo = this.findNodeForOffset(editor, frag.start);
        const endInfo = this.findNodeForOffset(editor, frag.end);
        
        if (!startInfo || !endInfo) {
          console.warn('无法映射偏移到 DOM 节点', frag);
          return;
        }

        if (startInfo.node !== endInfo.node) {
          console.warn('跨节点高亮，尝试分段处理:', frag);
          this.highlightAcrossNodes(editor, frag);
          return;
        }

        const range = document.createRange();
        try {
          range.setStart(startInfo.node, startInfo.nodeOffset);
          range.setEnd(endInfo.node, endInfo.nodeOffset);

          const span = document.createElement('span');
          span.className = `highlight-${frag.type || 'grammar'}`;
          span.setAttribute('data-issue-index', frag.issueIndex);
          if (frag.description) span.setAttribute('title', frag.description);
          
          range.surroundContents(span);

          span.addEventListener('click', (e) => {
            e.stopPropagation();
            this.highlightIssue(this.issues[frag.issueIndex], frag.issueIndex);
          });
          span.addEventListener('mouseenter', () => span.classList.add('hovered-highlight'));
          span.addEventListener('mouseleave', () => span.classList.remove('hovered-highlight'));
        } catch (err) {
          console.warn('range.surroundContents 失败，尝试分段处理:', frag, err);
          this.highlightAcrossNodes(editor, frag);
        }
      });
    },

    highlightAcrossNodes(editor, frag) {
      try {
        const walker = document.createTreeWalker(
          editor, 
          NodeFilter.SHOW_TEXT, 
          null, 
          false
        );
        
        let currentNode;
        let pos = 0;
        const nodesToWrap = [];

        while ((currentNode = walker.nextNode())) {
          const nodeLen = currentNode.nodeValue.length;
          const nodeStart = pos;
          const nodeEnd = pos + nodeLen;

          if (nodeEnd > frag.start && nodeStart < frag.end) {
            const relStart = Math.max(0, frag.start - nodeStart);
            const relEnd = Math.min(nodeLen, frag.end - nodeStart);
            
            nodesToWrap.push({
              node: currentNode,
              start: relStart,
              end: relEnd
            });
          }

          pos += nodeLen;
          if (pos >= frag.end) break;
        }

        nodesToWrap.reverse().forEach(({ node, start, end }) => {
          const range = document.createRange();
          range.setStart(node, start);
          range.setEnd(node, end);

          const span = document.createElement('span');
          span.className = `highlight-${frag.type || 'grammar'}`;
          span.setAttribute('data-issue-index', frag.issueIndex);
          if (frag.description) span.setAttribute('title', frag.description);

          try {
            range.surroundContents(span);
            
            span.addEventListener('click', (e) => {
              e.stopPropagation();
              this.highlightIssue(this.issues[frag.issueIndex], frag.issueIndex);
            });
            span.addEventListener('mouseenter', () => span.classList.add('hovered-highlight'));
            span.addEventListener('mouseleave', () => span.classList.remove('hovered-highlight'));
          } catch (e) {
            console.error('分段高亮失败:', e);
          }
        });
      } catch (err) {
        console.error('highlightAcrossNodes 失败:', err);
      }
    },

    checkContent() {
      this.isFocused = false
      const html = this.$refs.editor.innerHTML.trim()
      if (!html || html === '<br>') {
        this.$refs.editor.innerHTML = ''
      }
    },

    highlightIssue(issue, index) {
      const prevHighlights = this.$refs.editor.querySelectorAll('.highlight-active')
      prevHighlights.forEach(el => el.classList.remove('highlight-active'))
      
      const highlights = this.$refs.editor.querySelectorAll(`[data-issue-index="${index}"]`)
      highlights.forEach(el => {
        el.classList.add('highlight-active')
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      
      this.currentHighlightIndex = index
    },

    removeHighlight() {
      const prevHighlights = this.$refs.editor.querySelectorAll('.highlight-active')
      prevHighlights.forEach(el => el.classList.remove('highlight-active'))
      this.currentHighlightIndex = -1
    },

    scrollToIssue(index) {
      const issueItems = document.querySelectorAll('.issue-item')
      if (issueItems[index]) {
        issueItems[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        issueItems[index].style.backgroundColor = 'rgba(255, 235, 59, 0.3)'
        setTimeout(() => {
          issueItems[index].style.backgroundColor = ''
        }, 1000)
      }
    },

    acceptSuggestion(issue, index) {
      console.log('接受建议:', issue);
      
      const operationType = this.getOperationType(issue);
      console.log('操作类型:', operationType);
      
      const position = issue.exactPosition || issue.position;
      
      if (position && Array.isArray(position) && position.length === 2) {
        let [start, end] = position;
        
        const originalText = this.content.substring(start, end);
        console.log(`位置 [${start}, ${end}]`);
        console.log(`原文: "${originalText}"`);
        console.log(`issue.original: "${issue.original}"`);
        console.log(`issue.suggestion: "${issue.suggestion}"`);
        
        let newContent = '';
        let lengthDiff = 0;
        
        switch(operationType) {
          case 'delete':
            console.log('执行删除操作，删除内容:', originalText);
            newContent = this.content.substring(0, start) + this.content.substring(end);
            lengthDiff = -(end - start);
            break;
            
          case 'replace':
            console.log(`执行替换操作: "${originalText}" -> "${issue.suggestion}"`);
            newContent = this.content.substring(0, start) + 
                        issue.suggestion + 
                        this.content.substring(end);
            lengthDiff = issue.suggestion.length - (end - start);
            break;
            
          case 'warning':
            this.$message.info('这是一个警示提示，已忽略');
            this.issues.splice(index, 1);
            return;
            
          default:
            console.warn('未知操作类型');
            this.$message.warning('未知的操作类型');
            return;
        }
        
        console.log(`内容变化: 原长度=${this.content.length}, 变化=${lengthDiff}, 新长度=${newContent.length}`);
        
        this.content = newContent;
        
        if (lengthDiff !== 0) {
          const affectedPosition = end;
          
          this.issues.forEach((otherIssue, i) => {
            if (i !== index) {
              if (otherIssue.position && otherIssue.position[0] >= affectedPosition) {
                const oldPos = [...otherIssue.position];
                otherIssue.position[0] += lengthDiff;
                otherIssue.position[1] += lengthDiff;
                console.log(`更新 issue ${i} 的 position: [${oldPos[0]}, ${oldPos[1]}] -> [${otherIssue.position[0]}, ${otherIssue.position[1]}]`);
              }
              if (otherIssue.exactPosition) {
                if (otherIssue.exactPosition[0] >= affectedPosition) {
                  const oldExactPos = [...otherIssue.exactPosition];
                  otherIssue.exactPosition[0] += lengthDiff;
                  otherIssue.exactPosition[1] += lengthDiff;
                  console.log(`更新 issue ${i} 的 exactPosition: [${oldExactPos[0]}, ${oldExactPos[1]}] -> [${otherIssue.exactPosition[0]}, ${otherIssue.exactPosition[1]}]`);
                }
              }
            }
          });
        }
      } else {
        console.warn('issue 缺少 position 信息');
        this.$message.warning('无法应用建议：缺少位置信息');
        return;
      }
      
      this.wordCount = this.content.length;
      
      this.issues.splice(index, 1);
      
      this.$message.success('已应用修改建议');
      this.$nextTick(() => {
        this.$refs.editor.innerText = this.content;
        this.applyHighlighting();
      });
    },
    
    getOperationType(issue) {
      if (issue.type === 'error') {
        return 'delete';
      } else if (issue.type === 'warning') {
        return 'replace';
      } else if (issue.type === 'sensitive') {
        return 'replace';
      } else if (issue.type === 'punctuation') {
        return 'replace';
      } else if (issue.type === 'grammar') {
        if (issue.suggestion === '-') {
          return 'delete';
        }
        return 'warning';
      }
      
      if (issue.suggestion === '-') {
        return 'delete';
      }
      
      return 'replace';
    },

    ignoreIssue(index) {
      this.issues.splice(index, 1)
      this.$message.info('已忽略该建议')
      this.$nextTick(() => {
        this.applyHighlighting()
      })
    }
  }
}
</script>

<style scoped>
.contents {
  display: flex;
  min-height: 100vh;
  margin: 0 auto;
  background-color: #f5f7fa;
}

.left {
  flex: 7;
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 100vh;
  box-sizing: border-box;
  min-width: 0;
}

.right {
  flex: 3;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  word-wrap: break-word;
}

/* 工具栏样式 */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin: 10px 0;
}

.toolbar-left {
  display: flex;
  gap: 12px;
}

.action-btn {
  height: 40px;
  padding: 0 20px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.word-counter {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
}

.counter-text {
  color: #666;
  margin-right: 8px;
}

.counter-number {
  font-weight: bold;
  color: #409eff;
  font-size: 16px;
}

.counter-number.over-limit {
  color: #f56c6c;
}

.counter-limit {
  color: #999;
  margin-left: 2px;
}

/* 编辑器样式 */
.editor-container {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
}

.editor {
  height: 100%;
  padding: 24px;
  border: none;
  outline: none;
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  resize: none;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}

.editor:empty::before {
  content: attr(data-placeholder);
  color: #c0c4cc;
  font-style: italic;
}

.limit-warning {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fef0f0;
  color: #f56c6c;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  font-size: 14px;
  border-top: 1px solid #fbc4c4;
}

.limit-warning i {
  margin-right: 8px;
}

/* 右侧面板样式 */
.stats-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 10px 24px;
  margin-bottom: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.stats-header h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-item.total {
  text-align: center;
  border-radius: 12px;
  border: 2px solid #4851ea;
}

.stat-item.total .stat-number {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stat-item {
  text-align: center;
  padding: 5px 0;
  border-radius: 8px;
  background: white;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.stat-item .stat-number {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 4px;
}

.stat-item .stat-label {
  font-size: 12px;
  color: #666;
}

.stat-item.high-risk { border-color: #f56c6c; }
.stat-item.high-risk .stat-number { color: #f56c6c; }

.stat-item.low-risk { border-color: #e6a23c; }
.stat-item.low-risk .stat-number { color: #e6a23c; }

.stat-item.sensitive { border-color: #409eff; }
.stat-item.sensitive .stat-number { color: #409eff; }

.stat-item.punctuation { border-color: #909399; }
.stat-item.punctuation .stat-number { color: #909399; }

.stat-item.grammar { border-color: #67c23a; }
.stat-item.grammar .stat-number { color: #67c23a; }

/* 问题列表样式 */
.issues-section {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.issues-section h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.issues-list {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
}

.issue-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  background: white;
  border-radius: 12px;
  border-left: 4px solid transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  word-wrap: break-word;
  word-break: break-word;
}

.issue-item:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.issue-item.error {
  border-left-color: #f56c6c;
}

.issue-item.warning {
  border-left-color: #e6a23c;
}

.issue-item.sensitive {
  border-left-color: #409eff;
}

.issue-item.punctuation {
  border-left-color: #909399;
}

.issue-item.grammar {
  border-left-color: #67c23a;
}

.issue-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.issue-text {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-weight: 500;
  flex-wrap: wrap;
  gap: 4px;
}

.original-text {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  word-break: break-all;
  max-width: 45%;
}

.el-icon-arrow-right {
  flex-shrink: 0;
}

.suggested-text {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  word-break: break-all;
  max-width: 45%;
}

.issue-description {
  font-size: 12px;
  color: #666;
  word-break: break-word;
  margin-top: 4px;
}

.issue-actions {
  display: flex;
  gap: 8px;
}

.no-issues {
  text-align: center;
  padding: 40px 20px;
  color: #67c23a;
}

.no-issues i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.no-issues p {
  margin: 0;
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .contents {
    flex-direction: column;
  }
  
  .left, .right {
    flex: none;
  }
  
  .left {
    height: 60vh;
  }
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    gap: 16px;
  }
  
  .toolbar-left {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .action-btn {
    font-size: 14px;
    padding: 0 16px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* 鼠标悬停右侧时左侧对应错误高亮 */
.hovered-highlight {
  background-color: rgba(255, 235, 59, 0.4);
  border-radius: 4px;
  position: relative;
}

.hovered-highlight::after {
  content: "⚠";
  position: absolute;
  right: -12px;
  top: -8px;
  font-size: 14px;
  color: #f56c6c;
}
</style>

<style>
/* 高亮样式 - 根据错误类型显示不同颜色的下划线 */
.highlight-error {
  text-decoration: underline;
  text-decoration-color: #f56c6c;
  text-decoration-thickness: 2px;
  text-decoration-style: wavy;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.highlight-warning {
  text-decoration: underline;
  text-decoration-color: #e6a23c;
  text-decoration-thickness: 2px;
  text-decoration-style: wavy;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.highlight-sensitive {
  text-decoration: underline;
  text-decoration-color: #409eff;
  text-decoration-thickness: 2px;
  text-decoration-style: solid;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.highlight-punctuation {
  text-decoration: underline;
  text-decoration-color: #909399;
  text-decoration-thickness: 2px;
  text-decoration-style: dotted;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.highlight-grammar {
  text-decoration: underline;
  text-decoration-color: #67c23a;
  text-decoration-thickness: 2px;
  text-decoration-style: solid;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

/* 悬停时的高亮效果 */
.highlight-active {
  background-color: rgba(255, 235, 59, 0.5);
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
}
</style>